import { registerSW } from 'virtual:pwa-register';
import { hasActiveCriticalSession, subscribeCriticalSessions } from './criticalSessions';
import { appendAudit } from './audit';

type UpdateListener = (state: { updateReady: boolean; deferred: boolean; error: string | null }) => void;
let updateReady = false;
let deferred = false;
let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;
const listeners = new Set<UpdateListener>();

function emit(error: string | null = null) {
  const state = { updateReady, deferred, error };
  listeners.forEach(listener => { try { listener(state); } catch {} });
}

async function activateIfSafe(): Promise<void> {
  if (!updateReady || !updateSW) return;
  if (hasActiveCriticalSession()) {
    deferred = true;
    emit();
    return;
  }
  deferred = false;
  emit();
  await updateSW(true);
}

export function initializePwaUpdateCoordinator(): () => void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return () => {};
  updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      updateReady = true;
      void activateIfSafe();
    },
    onRegisteredSW(_swUrl, registration) {
      if (registration?.active?.scriptURL) localStorage.setItem('coconnect_sw_version', registration.active.scriptURL);
    },
    onRegisterError(error) {
      localStorage.setItem('coconnect_last_offline_error', 'SERVICE_WORKER_REGISTER_FAILED');
      emit(error instanceof Error ? error.message : String(error));
    },
  });

  const unsubscribe = subscribeCriticalSessions(sessions => {
    if (updateReady && sessions.length === 0) void activateIfSafe();
    else if (updateReady && sessions.length > 0) { deferred = true; emit(); }
  });
  return unsubscribe;
}

export function subscribePwaUpdate(listener: UpdateListener): () => void {
  listeners.add(listener);
  listener({ updateReady, deferred, error: null });
  return () => listeners.delete(listener);
}

export async function applyPendingPwaUpdate(): Promise<void> {
  await activateIfSafe();
}
